import { useState, useRef } from 'react';
import * as React from 'react';
import { FaDownload, FaUpload, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import { colors, shadows } from '../../employees/styles/personalakteStyles';
import RecruitingService from '../services/RecruitingService';
import type { ApplicantCreateData } from '../types';

interface ImportApplicantsModalProps {
  onClose: () => void;
  onImported: () => void;
}

const CSV_COLUMNS = [
  'first_name', 'last_name', 'email', 'phone',
  'street', 'house_no', 'zip_code', 'city',
  'team_choice', 'desired_employment_type', 'desired_start_date', 'desired_work_area',
  'hours_can_work', 'is_over_18',
  'native_language', 'german_level', 'english_level',
  'has_gastro_experience', 'gastro_experience_years',
  'has_red_card', 'is_already_employed', 'current_status',
  'favorite_ice_cream', 'cover_letter', 'motivation_text',
  'applied_at',
] as const;

const CSV_HEADER_LABELS: Record<string, string> = {
  first_name: 'Vorname', last_name: 'Nachname', email: 'E-Mail', phone: 'Telefon',
  street: 'Straße', house_no: 'Hausnr.', zip_code: 'PLZ', city: 'Stadt',
  team_choice: 'Team (SERVICE/PRODUCTION/MARKETING)', desired_employment_type: 'Beschäftigungsart (MINIJOB/WERKSTUDENT/TEILZEIT/VOLLZEIT)',
  desired_start_date: 'Gewünschter Start (YYYY-MM-DD)', desired_work_area: 'Arbeitsbereich',
  hours_can_work: 'Stunden/Woche', is_over_18: 'Über 18 (ja/nein)',
  native_language: 'Muttersprache', german_level: 'Deutsch-Level (A1-C2/Muttersprache)',
  english_level: 'Englisch-Level', has_gastro_experience: 'Gastro-Erfahrung (ja/nein)',
  gastro_experience_years: 'Gastro-Jahre', has_red_card: 'Rote Karte (ja/nein)',
  is_already_employed: 'Bereits beschäftigt (ja/nein)', current_status: 'Aktueller Status',
  favorite_ice_cream: 'Lieblingseis', cover_letter: 'Anschreiben', motivation_text: 'Motivation',
  applied_at: 'Bewerbungsdatum (YYYY-MM-DDTHH:MM:SS)',
};

const DEMO_ROWS = [
  ['Maria', 'Müller', 'maria.mueller@example.com', '+49 170 1234567', 'Musterstr.', '12', '10115', 'Berlin', 'SERVICE', 'WERKSTUDENT', '2026-04-01', 'Eiscafé Mitte', '20', 'ja', 'Deutsch', 'Muttersprache', 'B2', 'ja', '3', 'ja', 'nein', 'Studentin', 'Pistazie', 'Ich liebe Eis und möchte in eurem Team arbeiten!', 'Leidenschaft für Gastro', '2026-03-01T10:30:00'],
  ['Tom', 'Schmidt', 'tom.schmidt@example.com', '+49 151 9876543', 'Hauptstr.', '5a', '80331', 'München', 'PRODUCTION', 'MINIJOB', '2026-05-15', 'Produktion Süd', '10', 'ja', 'Englisch', 'B1', 'C1', 'nein', '', 'nein', 'nein', 'Schüler', 'Schokolade', '', 'Suche einen Nebenjob', ''],
];

function generateDemoCSV(): string {
  const sep = ';';
  const header = CSV_COLUMNS.map(c => CSV_HEADER_LABELS[c] || c).join(sep);
  const rows = DEMO_ROWS.map(r => r.map(v => {
    const s = String(v ?? '');
    return (s.includes(';') || s.includes('"') || s.includes('\n')) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(sep));
  return '\ufeff' + [header, ...rows].join('\r\n');
}

function downloadDemoCSV() {
  const csv = generateDemoCSV();
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bewerber_import_vorlage.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function parseCSV(text: string): string[][] {
  const lines: string[][] = [];
  let current: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        field += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ';' || ch === ',') {
        current.push(field.trim());
        field = '';
      } else if (ch === '\n') {
        current.push(field.trim());
        if (current.some(c => c !== '')) lines.push(current);
        current = [];
        field = '';
      } else if (ch === '\r') {
        // skip
      } else {
        field += ch;
      }
    }
  }
  current.push(field.trim());
  if (current.some(c => c !== '')) lines.push(current);
  return lines;
}

function parseBool(val: string): boolean | undefined {
  const v = val.toLowerCase().trim();
  if (['ja', 'yes', 'true', '1'].includes(v)) return true;
  if (['nein', 'no', 'false', '0'].includes(v)) return false;
  return undefined;
}

function mapRowToApplicant(row: string[], headerMap: Record<number, string>): ApplicantCreateData | null {
  const get = (key: string): string => {
    const idx = Object.entries(headerMap).find(([_, v]) => v === key)?.[0];
    return idx != null ? (row[Number(idx)] || '').trim() : '';
  };

  const firstName = get('first_name');
  const lastName = get('last_name');
  const email = get('email');
  if (!firstName || !lastName || !email) return null;

  const data: ApplicantCreateData = { first_name: firstName, last_name: lastName, email };

  const phone = get('phone'); if (phone) data.phone = phone;
  const street = get('street'); if (street) data.street = street;
  const houseNo = get('house_no'); if (houseNo) data.house_no = houseNo;
  const zipCode = get('zip_code'); if (zipCode) data.zip_code = zipCode;
  const city = get('city'); if (city) data.city = city;

  const team = get('team_choice').toUpperCase();
  if (team === 'SERVICE' || team === 'PRODUCTION' || team === 'MARKETING') data.team_choice = team;

  const empType = get('desired_employment_type').toUpperCase();
  if (['MINIJOB', 'WERKSTUDENT', 'TEILZEIT', 'VOLLZEIT'].includes(empType)) data.desired_employment_type = empType as any;

  const startDate = get('desired_start_date'); if (startDate) data.desired_start_date = startDate;
  const workArea = get('desired_work_area'); if (workArea) data.desired_work_area = workArea;

  const hours = get('hours_can_work'); if (hours) data.hours_can_work = Number(hours) || undefined;
  const over18 = parseBool(get('is_over_18')); if (over18 != null) data.is_over_18 = over18;

  const nativeLang = get('native_language'); if (nativeLang) data.native_language = nativeLang;
  const german = get('german_level'); if (german) data.german_level = german;
  const english = get('english_level'); if (english) data.english_level = english;

  const gastroExp = parseBool(get('has_gastro_experience')); if (gastroExp != null) data.has_gastro_experience = gastroExp;
  const gastroYears = get('gastro_experience_years'); if (gastroYears) data.gastro_experience_years = Number(gastroYears) || undefined;
  const redCard = parseBool(get('has_red_card')); if (redCard != null) data.has_red_card = redCard;
  const employed = parseBool(get('is_already_employed')); if (employed != null) data.is_already_employed = employed;

  const currentStatus = get('current_status'); if (currentStatus) data.current_status = currentStatus;
  const favoriteIceCream = get('favorite_ice_cream'); if (favoriteIceCream) data.favorite_ice_cream = favoriteIceCream;
  const coverLetter = get('cover_letter'); if (coverLetter) data.cover_letter = coverLetter;
  const motivation = get('motivation_text'); if (motivation) data.motivation_text = motivation;
  const appliedAt = get('applied_at'); if (appliedAt) data.applied_at = appliedAt;

  return data;
}

interface ImportResult {
  row: number;
  name: string;
  status: 'ok' | 'error';
  message: string;
}

export default function ImportApplicantsModal({ onClose, onImported }: ImportApplicantsModalProps): React.JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{ data: ApplicantCreateData; rowIndex: number }[]>([]);
  const [parseError, setParseError] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<ImportResult[]>([]);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setParseError('');
    setResults([]);
    setDone(false);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target?.result as string;
        const rows = parseCSV(text);
        if (rows.length < 2) {
          setParseError('Die Datei enthält keine Datenzeilen.');
          setPreview([]);
          return;
        }

        const headerRow = rows[0];
        const headerMap: Record<number, string> = {};

        headerRow.forEach((h, i) => {
          const normalized = h.toLowerCase().trim();
          const match = CSV_COLUMNS.find(c => {
            const label = (CSV_HEADER_LABELS[c] || c).toLowerCase();
            return normalized === c || normalized === label;
          });
          if (match) headerMap[i] = match;
        });

        const parsed: { data: ApplicantCreateData; rowIndex: number }[] = [];
        for (let r = 1; r < rows.length; r++) {
          const applicant = mapRowToApplicant(rows[r], headerMap);
          if (applicant) parsed.push({ data: applicant, rowIndex: r + 1 });
        }

        if (parsed.length === 0) {
          setParseError('Keine gültigen Zeilen gefunden. Mindestens Vorname, Nachname und E-Mail sind erforderlich.');
        }
        setPreview(parsed);
      } catch {
        setParseError('Fehler beim Lesen der Datei.');
        setPreview([]);
      }
    };
    reader.readAsText(f, 'utf-8');
  };

  const handleImport = async () => {
    if (preview.length === 0) return;
    setImporting(true);
    setResults([]);
    setProgress({ current: 0, total: preview.length });

    const importResults: ImportResult[] = [];

    for (let i = 0; i < preview.length; i++) {
      const { data, rowIndex } = preview[i];
      try {
        await RecruitingService.createApplicant(data);
        importResults.push({ row: rowIndex, name: `${data.first_name} ${data.last_name}`, status: 'ok', message: 'Angelegt' });
      } catch (e: any) {
        const msg = e?.data?.detail || e?.data?.email?.[0] || e?.message || 'Fehler';
        importResults.push({ row: rowIndex, name: `${data.first_name} ${data.last_name}`, status: 'error', message: msg });
      }
      setProgress({ current: i + 1, total: preview.length });
      setResults([...importResults]);
    }

    setImporting(false);
    setDone(true);

    const okCount = importResults.filter(r => r.status === 'ok').length;
    if (okCount > 0) onImported();
  };

  const okCount = results.filter(r => r.status === 'ok').length;
  const errCount = results.filter(r => r.status === 'error').length;

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(14, 19, 51, 0.4)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
      onClick={e => { if (e.target === e.currentTarget && !importing) onClose(); }}
    >
      <div style={{
        background: colors.bgCard, borderRadius: 16, padding: 28, width: 580,
        boxShadow: shadows.elevated, maxHeight: '85vh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
          Bewerber-Massenimport
        </h3>

        {/* Step 1: Download template + Upload file */}
        {!done && (
          <>
            <div style={{
              display: 'flex', gap: 12, marginBottom: 16, padding: 14, borderRadius: 10,
              background: colors.bgMuted, border: `1px solid ${colors.borderLight}`,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: colors.textBody, marginBottom: 4 }}>
                  1. Vorlage herunterladen
                </div>
                <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 8 }}>
                  CSV-Datei mit allen Feldern und 2 Beispielzeilen.
                </div>
                <button
                  onClick={downloadDemoCSV}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '6px 12px', borderRadius: 8,
                    border: `1px solid ${colors.border}`, background: colors.bgCard,
                    cursor: 'pointer', fontSize: 11, fontWeight: 500, color: colors.textBody,
                  }}
                >
                  <FaDownload style={{ fontSize: 10 }} /> bewerber_import_vorlage.csv
                </button>
              </div>
              <div style={{ width: 1, background: colors.border }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: colors.textBody, marginBottom: 4 }}>
                  2. Ausgefüllte Datei hochladen
                </div>
                <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 8 }}>
                  CSV mit Semikolon (;) als Trennzeichen.
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '6px 12px', borderRadius: 8,
                    border: `1px solid ${colors.slate}`, background: 'rgba(82, 113, 165, 0.06)',
                    cursor: importing ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 500, color: colors.slate,
                  }}
                >
                  <FaUpload style={{ fontSize: 10 }} /> {file ? file.name : 'CSV auswählen...'}
                </button>
              </div>
            </div>

            {parseError && (
              <div style={{ fontSize: 12, color: colors.danger, marginBottom: 12, padding: '8px 12px', background: colors.dangerLight, borderRadius: 8 }}>
                {parseError}
              </div>
            )}

            {/* Preview */}
            {preview.length > 0 && !importing && results.length === 0 && (
              <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: colors.textBody, marginBottom: 8 }}>
                  Vorschau: {preview.length} Bewerber erkannt
                </div>
                <div style={{ flex: 1, overflowY: 'auto', maxHeight: 250, borderRadius: 8, border: `1px solid ${colors.border}` }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: colors.bgMuted, position: 'sticky', top: 0 }}>
                        <th style={thSm}>Zeile</th>
                        <th style={thSm}>Name</th>
                        <th style={thSm}>E-Mail</th>
                        <th style={thSm}>Team</th>
                        <th style={thSm}>Typ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map(p => (
                        <tr key={p.rowIndex} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                          <td style={tdSm}>{p.rowIndex}</td>
                          <td style={tdSm}>{p.data.first_name} {p.data.last_name}</td>
                          <td style={tdSm}>{p.data.email}</td>
                          <td style={tdSm}>{p.data.team_choice || '–'}</td>
                          <td style={tdSm}>{p.data.desired_employment_type || '–'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Progress */}
            {importing && (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <FaSpinner style={{ fontSize: 20, color: colors.slate, animation: 'spin 1s linear infinite' }} />
                <div style={{ fontSize: 13, color: colors.textBody, marginTop: 8 }}>
                  Importiere... {progress.current} / {progress.total}
                </div>
                <div style={{
                  marginTop: 8, height: 6, borderRadius: 3, background: colors.bgMuted, overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%', borderRadius: 3,
                    background: 'linear-gradient(90deg, #5271A5, #3b5998)',
                    width: `${(progress.current / progress.total) * 100}%`,
                    transition: 'width 0.3s',
                  }} />
                </div>
              </div>
            )}

            {/* Inline results during/after import */}
            {results.length > 0 && (
              <div style={{ flex: 1, overflowY: 'auto', maxHeight: 250, marginTop: 12, borderRadius: 8, border: `1px solid ${colors.border}` }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: colors.bgMuted, position: 'sticky', top: 0 }}>
                      <th style={thSm}>Zeile</th>
                      <th style={thSm}>Name</th>
                      <th style={thSm}>Status</th>
                      <th style={thSm}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${colors.borderLight}`, background: r.status === 'error' ? 'rgba(239,68,68,0.04)' : 'transparent' }}>
                        <td style={tdSm}>{r.row}</td>
                        <td style={tdSm}>{r.name}</td>
                        <td style={tdSm}>
                          {r.status === 'ok'
                            ? <FaCheckCircle style={{ color: colors.success, fontSize: 12 }} />
                            : <FaTimesCircle style={{ color: colors.danger, fontSize: 12 }} />
                          }
                        </td>
                        <td style={{ ...tdSm, color: r.status === 'error' ? colors.danger : colors.textMuted }}>{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Done summary */}
        {done && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <FaCheckCircle style={{ fontSize: 32, color: colors.success, marginBottom: 12 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary, marginBottom: 4 }}>
              Import abgeschlossen
            </div>
            <div style={{ fontSize: 13, color: colors.textMuted }}>
              {okCount} erfolgreich angelegt
              {errCount > 0 && <span style={{ color: colors.danger }}>, {errCount} fehlgeschlagen</span>}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            onClick={onClose}
            disabled={importing}
            style={{
              padding: '8px 16px', borderRadius: 8, border: `1px solid ${colors.border}`,
              background: colors.bgCard, cursor: importing ? 'not-allowed' : 'pointer',
              fontSize: 12, fontWeight: 500,
            }}
          >
            {done ? 'Schließen' : 'Abbrechen'}
          </button>
          {!done && preview.length > 0 && (
            <button
              onClick={handleImport}
              disabled={importing || preview.length === 0}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg, #5271A5, #3b5998)', color: 'white',
                cursor: importing ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600,
                opacity: importing ? 0.7 : 1,
              }}
            >
              {importing ? `Importiere... (${progress.current}/${progress.total})` : `${preview.length} Bewerber importieren`}
            </button>
          )}
        </div>

        {/* Spinner animation */}
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

const thSm: React.CSSProperties = { padding: '6px 8px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748b' };
const tdSm: React.CSSProperties = { padding: '5px 8px', fontSize: 11 };
